import React, { useEffect, useState } from 'react';
import { FiSearch, FiEye, FiCheck, FiX, FiTrash2 } from 'react-icons/fi';
import { adminApi } from '../../services/adminApi';

function AdminFarmers() {
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (searchDebounced) params.search = searchDebounced;
    adminApi.getFarmers(params)
      .then((res) => {
        if (res.success) {
          setFarmers(res.data || []);
          setError(null);
        } else setError(res.message || 'Failed to load');
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setFarmers([]);
      })
      .finally(() => setLoading(false));
  }, [filter, searchDebounced]);

  const handleStatus = async (id, status) => {
    setUpdating(id);
    try {
      const res = await adminApi.updateFarmerStatus(id, status);
      if (res.success) {
        setFarmers((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete farmer "${name || id}"? This removes vouchers and financing links for this record.`)) return;
    setUpdating(id);
    try {
      const res = await adminApi.deleteFarmer(id);
      if (res.success) {
        setFarmers((prev) => prev.filter((f) => f.id !== id));
        setError(null);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : '-';

  return (
    <div className="admin-page">
      <div className="admin-page-header admin-page-header-row">
        <div>
          <h1>Farmer Registrations</h1>
          <p>Review and approve farmer onboarding requests (registered via WhatsApp)</p>
        </div>
      </div>
      <div className="admin-toolbar">
        <div className="admin-search">
          <FiSearch className="admin-search-icon" />
          <input
            type="search"
            placeholder="Search farmers..."
            className="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="admin-filter-tabs">
          {['all', 'pending', 'approved', 'rejected'].map((tab) => (
            <button
              key={tab}
              className={`admin-filter-tab ${filter === tab ? 'active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="admin-table-card">
        {loading ? (
          <div className="admin-loading" />
        ) : error ? (
          <p className="admin-error">{error}</p>
        ) : farmers.length === 0 ? (
          <p className="admin-empty">No farmers found</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Location</th>
                <th>Crop / Livestock</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {farmers.map((f) => (
                <tr key={f.id}>
                  <td><code>F{String(f.id).padStart(3, '0')}</code></td>
                  <td>{f.full_name}</td>
                  <td>{f.location || '-'}</td>
                  <td>{f.crop || '-'}</td>
                  <td>
                    <span className={`admin-badge admin-badge-${f.status}`}>
                      {f.status}
                    </span>
                  </td>
                  <td>{formatDate(f.created_at)}</td>
                  <td>
                    <div className="admin-table-actions">
                      <button className="admin-btn-icon" title="View">
                        <FiEye />
                      </button>
                      {f.status === 'pending' && (
                        <>
                          <button
                            className="admin-btn-icon admin-btn-success"
                            title="Approve"
                            onClick={() => handleStatus(f.id, 'approved')}
                            disabled={updating === f.id}
                          >
                            <FiCheck />
                          </button>
                          <button
                            className="admin-btn-icon admin-btn-danger"
                            title="Reject"
                            onClick={() => handleStatus(f.id, 'rejected')}
                            disabled={updating === f.id}
                          >
                            <FiX />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="admin-btn-icon admin-btn-danger"
                        title="Delete record"
                        onClick={() => handleDelete(f.id, f.full_name)}
                        disabled={updating === f.id}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminFarmers;
